/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Services from "../../services/services";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import SubscriptionPriceCard from "../../components/SubscriptionPriceCard";
import Title from "../../components/Title";
import { getConcurrencyByLimit, getUnitPriceByRequestCount, handleGetFreemiumSubscription, RoutePages, USD_TO_VND_RATE, } from "../../ultils";
import PriceUpdateAnnouncement from "./PriceUpdateAnnouncement";

const generatePriceByLimitData = (base_price_vnd) => {
  const data = [];
  let high = 100_000;
  for (let i = 1; i <= high; i += Math.round(high / 100)) {
    data.push({
      requests_limit: i,
      unit_price: getUnitPriceByRequestCount(i, base_price_vnd) / USD_TO_VND_RATE,
    });
  }
  return data;
};
const generateConcurrencyByLimitData = (base_price_vnd) => {
  const data = [];
  let high = 100_000;
  for (let i = 1; i <= high; i += Math.round(high / 100)) {
    data.push({
      requests_limit: i,
      concurrency: getConcurrencyByLimit(i, base_price_vnd),
    });
  }
  return data;
};

const FBGraphPricing = () => {
  const [requestsLimit, setRequestsLimit] = useState(1000);
  const [totalMoney, setTotalMoney] = useState(1.0);
  const [previewSub, setPreviewSub] = useState(null);
  const [exampleSubs, setExampleSubs] = useState([]);
  const [customMode, setCustomMode] = useState("requests");
  const [priceLogs, setPriceLogs] = useState([]);
  const [basePriceVND, setBasePriceVND] = useState(0);
  const [basePriceUSD, setBasePriceUSD] = useState(0);

  const navigate = useNavigate();
  const userStore = useSelector((state) => state.auth.user);

  const fetchExampleSubs = async () => {
    const limit_list = [10000, 100000, 500000];
    const promises = limit_list.map(async (limit) => {
      const res = await Services.Subscription.preview("fb_graph", limit, null);
      const data = await res.json();
      if (res.ok && data.success) {
        return data.data;
      }
      return null;
    });
    const results = await Promise.all(promises);
    setExampleSubs(results.filter((item) => item !== null));
  };

  const handlePreviewSubs = async (isUseRequestLimit) => {
    var res;
    if (isUseRequestLimit)
      res = await Services.Subscription.preview(
        "fb_graph",
        requestsLimit,
        null
      );
    else
      res = await Services.Subscription.preview("fb_graph", null, totalMoney);
    const data = await res.json();
    if (res.ok && data.success) {
      setPreviewSub(data.data);
    } else {
      toast.error(`code_${res.status}: ${data.error}`);
    }
  };

  useEffect(() => {
    handlePreviewSubs(true);
    fetchExampleSubs();
    fetchPriceLogs();
  }, []);

  useEffect(() => {
    fetchExampleSubs();
    setBasePriceVND(priceLogs[priceLogs.length - 1]?.basePriceVND);
  }, [priceLogs]);

  useEffect(() => {
    setBasePriceUSD(basePriceVND / USD_TO_VND_RATE);
  }, [basePriceVND])

  useEffect(() => {
    if (customMode === "requests") {
      handlePreviewSubs(true);
    } else {
      handlePreviewSubs(false);
    }
  }, [requestsLimit, totalMoney, customMode]);

  const fetchPriceLogs = async () => {
    const res = await Services.priceLogs();
    const data = await res.json();
    if (res.ok && data.success) {
      setPriceLogs(data.data);
    }
  };

  return (
    <div className="flex flex-col gap-8 p-2 sm:p-5 bg-gradient-to-br from-blue-100 via-white to-green-100 min-h-screen">
      <Title page={RoutePages.PRICING.FB_GRAPH} title="Price FB-Graph" />
      <div className="flex flex-col lg:flex-row gap-8 w-full max-w-7xl mx-auto">
        {/* Left column: Freemium & Calculator */}
        <div className="w-full lg:w-1/3 flex flex-col gap-8">
          {/** Documentation */}
          <div className="bg-white p-6 rounded-2xl shadow-lg border flex flex-col items-center animate-fade-in">
            <h2 className="text-2xl mb-3 font-extrabold text-center text-blue-600 tracking-wide">
              Documentations
            </h2>
            <button
              onClick={() => window.open(RoutePages.DOCS.FB_GRAPH.path, "_blank")}
              className="px-4 py-3 text-white text-base font-semibold rounded-lg bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 shadow transition-all duration-200 w-full"
            >
              View Docs
            </button>
          </div>

          {/* Freemium Subscription */}
          <div className="bg-white p-6 rounded-2xl shadow-lg border flex flex-col items-center animate-fade-in">
            <h2 className="text-2xl mb-3 font-extrabold text-center text-green-600 tracking-wide">
              Free Trial
            </h2>
            <div className="flex flex-col items-center gap-1 mb-4">
              <span className="text-lg font-semibold text-gray-700">
                300 requests/month
              </span>
              <span className="text-lg font-semibold text-gray-700">
                50 concurrency
              </span>
            </div>
            <button
              onClick={() => handleGetFreemiumSubscription("fb_graph", userStore, navigate)}
              className="px-4 py-3 text-white text-base font-semibold rounded-lg bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 shadow transition-all duration-200 w-full"
            >
              Get Free Trial
            </button>
          </div>
          {/* Price Calculator */}
          <div className="bg-white p-6 rounded-2xl shadow-lg border flex flex-col gap-4 animate-fade-in">
            <h2 className="text-2xl font-extrabold text-center text-blue-600 tracking-wide">
              Custom Plan Calculator
            </h2>
            <div className="flex flex-col gap-4 justify-center items-center">
              {/* Toggle button giống Dashboard */}
              <div className="flex items-center gap-2 sm:gap-4">
                <span
                  className={`w-32 text-center text-base sm:text-lg text-gray-700 ${customMode === "requests" ? "font-bold" : ""
                    }`}
                >
                  By Requests Limit
                </span>
                <button
                  onClick={() => {
                    const nextMode =
                      customMode === "requests" ? "price" : "requests";
                    setCustomMode(nextMode);
                    handlePreviewSubs(nextMode === "requests");
                  }}
                  className={`w-12 sm:w-16 h-8 rounded-full transition-colors ${customMode === "price" ? "bg-blue-500" : "bg-green-500"
                    } relative`}
                  type="button"
                >
                  <span
                    className={`absolute left-1 top-1 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${customMode === "price"
                      ? "translate-x-4 sm:translate-x-8"
                      : ""
                      }`}
                  ></span>
                </button>
                <span
                  className={`w-32 text-center text-base sm:text-lg text-gray-700 ${customMode === "price" ? "font-bold" : ""
                    }`}
                >
                  By Monthly Price (USD)
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-5 justify-center items-center w-full mt-2">
                {/* Input requests limit */}
                <div
                  className={`flex flex-col items-center transition-opacity duration-200 ${customMode !== "requests" ? "opacity-50" : ""
                    }`}
                >
                  <input
                    type="number"
                    className="w-28 mb-1 p-2 border-2 border-cyan-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={requestsLimit}
                    min={2}
                    inputMode="numeric"
                    disabled={customMode !== "requests"}
                    onChange={(e) => setRequestsLimit(Number(e.target.value))}
                  />
                  <span className="text-xs text-gray-500">Requests limit</span>
                </div>
                {/* Input price */}
                <div
                  className={`flex flex-col items-center transition-opacity duration-200 ${customMode !== "price" ? "opacity-50" : ""
                    }`}
                >
                  <input
                    type="number"
                    className="w-28 mb-1 p-2 border-2 border-cyan-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={totalMoney}
                    min={1}
                    step={0.01}
                    inputMode="decimal"
                    disabled={customMode !== "price"}
                    onChange={(e) => setTotalMoney(Number(e.target.value))}
                  />
                  <span className="text-xs text-gray-500">
                    Monthly Price (USD)
                  </span>
                </div>
              </div>
              <span className="text-base font-bold text-center">
                {USD_TO_VND_RATE.toLocaleString()} VND = 1 USD
                <br />
              </span>
            </div>
            {previewSub && (
              <div className="mt-2 animate-fade-in-up">
                <SubscriptionPriceCard
                  scheme="blue"
                  title="Custom Plan"
                  service_callback="fb_graph"
                  requests_limit={previewSub.requests_limit}
                  concurrency={previewSub.concurrency}
                  unit_price={previewSub.unit_price}
                  price={previewSub.price}
                  started_at={previewSub.started_at}
                  expired_at={previewSub.expired_at}
                />
              </div>
            )}
          </div>
        </div>
        {/* Right column: Examples & Charts */}
        <div className="w-full lg:w-2/3 flex flex-col gap-8">
          <PriceUpdateAnnouncement
            updates={priceLogs} />
          {/* Example Subscriptions */}
          <div className="w-full flex flex-col xl:flex-row gap-6 justify-center">
            {exampleSubs.map((item, idx) => (
              <div
                key={idx}
                className="flex-1 min-w-[220px] animate-fade-in-up"
              >
                <SubscriptionPriceCard
                  scheme="blue"
                  title={`FB Graph #${idx + 1}`}
                  service_callback="fb_graph"
                  requests_limit={item.requests_limit}
                  concurrency={item.concurrency}
                  unit_price={item.unit_price}
                  price={item.price}
                  started_at={item.started_at}
                  expired_at={item.expired_at}
                />
              </div>
            ))}
          </div>
          {/* Price Formula */}
          <div className="w-full h-fit p-4 sm:p-6 shadow border rounded-2xl bg-white animate-fade-in">
            <h2 className="text-xl font-bold mb-2 text-blue-700">
              Unit Price depends on Requests Limit
            </h2>
            <h3 className="text-base font-semibold mb-4 text-gray-600">
              Unit_Price = <strong>{basePriceUSD.toFixed(5)}</strong> + 11430 / (Requests_Limit + 10000)
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                data={generatePriceByLimitData(basePriceVND)}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="requests_limit"
                  label={{
                    value: "Monthly requests limit",
                    position: "insideBottomRight",
                    offset: 0,
                  }}
                />
                <YAxis
                  label={{
                    value: "Unit Price (USD)",
                    position: "insideTopRight",
                    angle: -90,
                    offset: 10,
                  }}
                />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="unit_price"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Concurrency Formula */}
          <div className="w-full h-fit p-4 sm:p-6 shadow border rounded-2xl bg-white animate-fade-in">
            <h2 className="text-xl font-bold mb-2 text-green-700">
              Concurrency depends on Requests Limit
            </h2>
            <h3 className="text-base font-semibold mb-4 text-gray-600">
              Concurrency = 5.2 * log8((10*(Limit^2) + 300,000*Limit)/(Limit + 10,000)) - 26
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                data={generateConcurrencyByLimitData(basePriceVND)}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="requests_limit"
                  label={{
                    value: "Monthly requests limit",
                    position: "insideBottomRight",
                    offset: 0,
                  }}
                />
                <YAxis
                  label={{
                    value: "Concurrency Requests Allowed",
                    position: "insideTopRight",
                    angle: -90,
                    offset: 10,
                  }}
                />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="concurrency"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FBGraphPricing;
